// src/pages/playlists/PlaylistTip.jsx
// One-time coach mark for the Audio and Video archives, anchored to the first
// visible save-to-playlist button: that row stays lit, the rest of the page is
// dimmed, and a popover points at the button. Only "Got it" and "Open
// Playlists" dismiss it for good (account-level, the same store TipBar uses).
// Tapping the highlighted button or pressing Esc just hides it until the page
// is opened again.
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { isTipDismissed, dismissTip } from "../../components/tipbar/dismissTip";
import "./playlists.css";

const TIP_ID = "playlist-save";
const GAP = 14; // between the button and the popover
const EDGE = 16; // min distance from the viewport edge
const SPOT_PAD = 4; // clear space around the highlighted row

// The archive renders both a desktop and a phone layout; take the button that
// is actually on screen.
function findTarget() {
  return [...document.querySelectorAll(".pls-savebtn")].find((el) => el.getClientRects().length > 0) || null;
}

export default function PlaylistTip() {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const navigate = useNavigate();
  const dismissed = isTipDismissed(user, TIP_ID);

  const [target, setTarget] = useState(null);
  const [pos, setPos] = useState(null);
  const [hidden, setHidden] = useState(false); // closed for this visit only
  const popRef = useRef(null);

  // Wait for the list to render, then pick the first save button.
  useEffect(() => {
    if (!user || dismissed || hidden) return undefined;
    let tries = 0;
    let timer = setTimeout(function look() {
      const el = findTarget();
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setTarget(el);
      } else if (++tries < 30) {
        timer = setTimeout(look, 300);
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [user, dismissed, hidden]);

  // For now: hide until the page is opened again.
  const hide = useCallback(() => {
    setHidden(true);
    setTarget(null);
  }, []);

  // For good: "Got it" / "Open Playlists".
  const close = useCallback(() => {
    dismissTip(TIP_ID, auth?.user, auth?.setUser);
    hide();
  }, [auth, hide]);

  // Highlight the button; tapping it (to save) or Esc just hides the tip.
  useEffect(() => {
    if (!target) return undefined;
    target.classList.add("pls-coach-target");
    const onTap = () => hide();
    const onKey = (e) => e.key === "Escape" && hide();
    target.addEventListener("click", onTap);
    document.addEventListener("keydown", onKey);
    return () => {
      target.classList.remove("pls-coach-target");
      target.removeEventListener("click", onTap);
      document.removeEventListener("keydown", onKey);
    };
  }, [target, hide]);

  // Follow the button through scrolling and resizing.
  const place = useCallback(() => {
    if (!target || !target.isConnected) return;
    const b = target.getBoundingClientRect();
    const row = (target.closest(".audio-item, .yh-row") || target).getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(vw < 600 ? 250 : 300, vw - EDGE * 2);
    const h = popRef.current?.offsetHeight || (vw < 600 ? 130 : 190);
    const cx = b.left + b.width / 2;
    const left = Math.max(EDGE, Math.min(cx - w + 44, vw - w - EDGE));
    const below = b.bottom + GAP + h <= vh - EDGE || b.top - GAP - h < EDGE;
    setPos({
      // A few px of breathing room around the lit row.
      row: {
        top: row.top - SPOT_PAD,
        left: row.left - SPOT_PAD,
        right: row.right + SPOT_PAD,
        bottom: row.bottom + SPOT_PAD,
      },
      pop: { left, width: w, top: below ? b.bottom + GAP : b.top - GAP - h },
      arrowX: Math.max(20, Math.min(cx - left, w - 20)),
      below,
    });
  }, [target]);

  useLayoutEffect(() => {
    if (!target) return undefined;
    let frame = 0;
    const onMove = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [target, place]);

  // Re-place once the popover has a real height.
  useLayoutEffect(() => {
    if (pos && popRef.current) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!pos]);

  if (!target || !pos || dismissed) return null;

  // Four frosted panels around the row (above, below, left, right): they fade
  // and blur the rest of the page while the row itself stays sharp.
  const r = pos.row;
  const dims = [
    { top: 0, left: 0, right: 0, height: Math.max(0, r.top) },
    { top: r.bottom, left: 0, right: 0, bottom: 0 },
    { top: r.top, left: 0, width: Math.max(0, r.left), height: r.bottom - r.top },
    { top: r.top, left: r.right, right: 0, height: r.bottom - r.top },
  ];

  return createPortal(
    <>
      {dims.map((style, i) => (
        <div key={i} className="pls-coach-dim" aria-hidden="true" style={style} />
      ))}
      <div
        ref={popRef}
        className={`pls-coach ${pos.below ? "below" : "above"}`}
        role="dialog"
        aria-label="Save to a playlist"
        style={{ top: pos.pop.top, left: pos.pop.left, width: pos.pop.width, "--arrow-x": `${pos.arrowX}px` }}
      >
        <div className="pls-coach-kicker">New</div>
        <div className="pls-coach-title">Save to a playlist</div>
        <p className="pls-coach-body">Tap this on any lecture to keep it in a playlist for later.</p>
        <div className="pls-coach-actions">
          <button type="button" className="pls-coach-link" onClick={() => { close(); navigate("/playlists"); }}>
            Open Playlists
          </button>
          <button type="button" className="pls-coach-ok" onClick={close}>
            Got it
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
